import useQueries from "@/hooks/useQueries";
import { API_URL } from "@/shared/utils/API_URL";
import { buildQuery } from "@/shared/utils/buildQuery";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import Cookies from 'js-cookie';
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import useEntities from "./createOrder/useEntities";
import useDateFilter from "./useDateFilter";
import useLogsController from "./useLogsController";
import { useTranslation } from "react-i18next";
import { useDebounce } from "./useDebounce";


const fetchSalesProfits = async (sessionId: any, salesProfitsQuery: string, licenceId: any) => {
    const response = await axios.post(
        API_URL,
        { q: salesProfitsQuery },
        {
            headers: { 'Content-Type': 'application/json' },
            params: {
                t: 'sel',
                phone: '',
                id: licenceId,
                s: sessionId,
                btn: 'w_itm',
                api: 'itqan'
            },
        }
    );
    return response.data;
};

const fetchData = async (sessionId: string | undefined, query: string, licenceId: string | null) => {
    const response = await axios.post(
        API_URL,
        { q: query },
        {
            headers: { 'Content-Type': 'application/json' },
            params: { t: 'sel', phone: '', id: licenceId, s: sessionId, btn: 'w_itm', api: 'itqan' },
        }
    );
    return response.data;
};



const executeCancelOrder = async ({ sessionId, cancelOrderQuery, licenceId }: any) => {
    const response = await axios.post(
        API_URL,
        { q: cancelOrderQuery },
        {
            headers: { 'Content-Type': 'application/json' },
            params: { t: 'upd', phone: '', id: licenceId, s: sessionId, btn: 'w_itm', api: 'itqan' },
        }
    );
    return response.data;
};

const escapeSqlValue = (value: string) => value.replace(/'/g, "''");

const PAGE_SIZE = 20;


export default function useSalesProfitController() {
    const { t } = useTranslation();
    const queries = useQueries();
    const sessionId = Cookies.get('sessionId');
    const licenceId = localStorage.getItem("licence_id");
    const { log } = useLogsController();

    const [selectedProject, setSelectedProject] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const queryClient = useQueryClient();
    const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; orderId: number | null }>({ open: false, orderId: null });
    const [page, setPage] = useState(0);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');


    const { projectsWithBusiness } = useEntities();

    const { date, setDate, handleQuickSelect } = useDateFilter();

    const debouncedSearchValue = useDebounce(searchValue, 1000);


    const filters = useMemo(() => {
        const conditions: string[] = [];

        if (date.from) {
            const fromQuery = date.from.includes(' ') ? date.from : `${date.from} 00:00:00`;
            conditions.push(`AND orders.orderdate >= '${escapeSqlValue(fromQuery)}'`);
        }
        if (date.to) {
            const toQuery = date.to.includes(' ') ? date.to : `${date.to} 23:59:59`;
            conditions.push(`AND orders.orderdate <= '${escapeSqlValue(toQuery)}'`);
        }
        if (selectedProject) {
            conditions.push(`AND CurrId = '${escapeSqlValue(selectedProject)}'`);
        }
        if (debouncedSearchValue.trim()) {
            const s = escapeSqlValue(debouncedSearchValue.trim());
            conditions.push(`AND (CAST(orders.orderid AS CHAR) LIKE '%${s}%' OR accounts.accountstring LIKE '%${s}%' OR accounts.tel1 LIKE '%${s}%')`);
        }

        return conditions.join(' ');
    }, [date, selectedProject, debouncedSearchValue]);


    const moneyFilters = useMemo(() => {
        const conditions: string[] = [];

        if (date.from) {
            const fromQuery = date.from.includes(' ') ? date.from : `${date.from} 00:00:00`;
            conditions.push(`AND money.moneydate >= '${escapeSqlValue(fromQuery)}'`);
        }
        if (date.to) {
            const toQuery = date.to.includes(' ') ? date.to : `${date.to} 23:59:59`;
            conditions.push(`AND money.moneydate <= '${escapeSqlValue(toQuery)}'`);
        }
        if (selectedProject) {
            conditions.push(`AND money.moneyprjid = '${escapeSqlValue(selectedProject)}'`);
        }

        return conditions.join(' ');
    }, [date, selectedProject]);

    const summaryFilters = useMemo(() => {
        const conditions: string[] = [];
        if (date.from) {
            const fromQuery = date.from.includes(' ') ? date.from : `${date.from} 00:00:00`;
            conditions.push(`AND orderdate >= '${escapeSqlValue(fromQuery)}'`);
        }
        if (date.to) {
            const toQuery = date.to.includes(' ') ? date.to : `${date.to} 23:59:59`;
            conditions.push(`AND orderdate <= '${escapeSqlValue(toQuery)}'`);
        }
        if (selectedProject) {
            conditions.push(`AND CurrId = '${escapeSqlValue(selectedProject)}'`);
        }
        return conditions.join(' ');
    }, [date, selectedProject]);

    useEffect(() => {
        setPage(0);
    }, [debouncedSearchValue, selectedProject, date, sortOrder]);

    const salesProfitsQuery = queries?.salesProfitsQuery
        ? buildQuery(queries.salesProfitsQuery, {
            filters,
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE,
            sort_order: sortOrder === 'asc' ? 'ASC' : 'DESC',
        })
        : '';

    const salesProfitsCountQuery = queries?.salesProfitsCountQuery
        ? buildQuery(queries.salesProfitsCountQuery, { filters })
        : '';


    const canQuery = !!queries;

    const salesSummaryQuery = canQuery ? buildQuery(queries!.salesSummaryQuery, { filters: summaryFilters }) : '';
    const itemsSoldRankedQuery = canQuery ? buildQuery(queries!.itemsSoldRankedQuery, { filters: summaryFilters }) : '';

    const { data: itemsRankedData, isLoading: isItemsRankedLoading } = useQuery({
        queryKey: ['itemsSoldRanked', itemsSoldRankedQuery, sessionId, licenceId],
        queryFn: () => fetchData(sessionId, itemsSoldRankedQuery, licenceId),
        enabled: !!itemsSoldRankedQuery && !!sessionId && !!licenceId,
        staleTime: 0,
        refetchOnMount: true,
    });

    const itemsSoldRanked = Array.isArray(itemsRankedData?.data?.result) ? itemsRankedData.data.result : [];

    const { data: summaryData, isLoading: isSummaryLoading } = useQuery({
        queryKey: ['itemSalesSummary', salesSummaryQuery, sessionId, licenceId],
        queryFn: () => fetchData(sessionId, salesSummaryQuery, licenceId),
        enabled: !!salesSummaryQuery && !!sessionId && !!licenceId,
        staleTime: 0,
        refetchOnMount: true,
    });

    const summary = Array.isArray(summaryData?.data?.result) ? summaryData?.data?.result?.[0] : summaryData;


    const { data, isLoading, isFetching } = useQuery({
        queryKey: ['sales_profits', salesProfitsQuery, date.from, date.to, licenceId, sessionId, sortOrder],
        queryFn: () => fetchSalesProfits(sessionId, salesProfitsQuery, licenceId),
        enabled: !!salesProfitsQuery && !!sessionId && !!licenceId,
        staleTime: 0,
        refetchOnMount: true,
        placeholderData: (prev: any) => prev,
    });

    const { data: countData } = useQuery({
        queryKey: ['sales_profits_count', salesProfitsCountQuery, date.from, date.to, selectedProject, searchValue, licenceId, sessionId],
        queryFn: () => fetchSalesProfits(sessionId, salesProfitsCountQuery, licenceId),
        enabled: !!salesProfitsCountQuery && !!sessionId && !!licenceId,
        staleTime: 0,
    });

    const rawResult = countData?.data?.result?.[0];
    const totalCount = Number(rawResult?.total_count || rawResult?.total || rawResult?.count || 0);

    const salesProfits = data?.data?.result || [];
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);
    const activeSalesProfits = salesProfits?.filter((s: any) => s?.manualid !== 'cancelled');


    const { mutate: cancelOrder, isPending: isCancelling } = useMutation({
        mutationFn: ({ orderId }: { orderId: number }) => {
            const cancelOrderQuery = buildQuery(queries.cancelOrderQuery, { order_id: orderId });
            return executeCancelOrder({ sessionId, cancelOrderQuery, licenceId });
        },
        onSuccess: (_, variables) => {
            toast.success("تم إلغاء الطلبية بنجاح");
            const targetOrder = salesProfits.find((s: any) => Number(s?.order_id) === Number(variables.orderId));
            log({
                action: "DELETE",
                opname: "cancelOrder",
                title: `تم إلغاء الطلبية #${variables.orderId}`,
                refid: variables.orderId ? Number(variables.orderId) : 0,
                ltype: "ORDER",
                ldata: {
                    orderId: variables.orderId,
                    accountName: targetOrder?.accoutn_name,
                    totalAmount: targetOrder?.grand_total || targetOrder?.total || 0,
                    projectId: targetOrder?.project_id,
                    timestamp: new Date().toISOString()
                },
                lnote: "ORDER_CANCELLED"
            });

            queryClient.invalidateQueries({ queryKey: ['sales_profits'] });
            setConfirmDialog({ open: false, orderId: null });
        },
        onError: () => {
            toast.error("فشل إلغاء الطلبية، حاول مجدداً");
            setConfirmDialog({ open: false, orderId: null });
        },
    });

    const askCancelOrder = (orderId: number) => setConfirmDialog({ open: true, orderId });

    const confirmCancelOrder = () => {
        if (!confirmDialog.orderId) return;
        cancelOrder({ orderId: confirmDialog.orderId });
    };

    const dismissDialog = () => setConfirmDialog({ open: false, orderId: null });


    const handleExport = (format: 'pdf' | 'excel', customTitle = 'تقرير المبيعات والأرباح') => {
        if (!salesProfitsQuery) {
            toast.error(t("no_query_available"));
            return;
        }

        const cleanQuery = salesProfitsQuery
            .replace(/\s+LIMIT\s+\d+/gi, '')
            .replace(/\s+OFFSET\s+\d+/gi, '');

        const exportColumns = [
            { key: 'order_id', label: t('order_id') },
            { key: 'accoutn_name', label: t('account_name') },
            { key: 'order_date', label: t('order_date') },
            { key: 'item_name', label: t('item_name') },
            { key: 'order_quantity', label: t('order_quantity') },
            { key: 'sell_price', label: t('sell_price') },
            { key: 'cost_price', label: t('cost_price') },
            { key: 'pcs_profit', label: t('pcs_profit') },
            { key: 'total_profit', label: t('total_profit') },
        ];

        const storeName = selectedProject
            ? (projectsWithBusiness?.find((p: any) => String(p.business_id) === String(selectedProject))?.type_name || '—')
            : t('all_stores', 'كل المتاجر');

        const fmt = (v: any) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
        const netProfit = totalProfitAmount - totalExpensesAmount;

        const summaryPayload = [
            { label: t('store', 'المتجر'), value: storeName },
            { label: t('date_range', 'الفترة'), value: `${date.from || '—'} إلى ${date.to || '—'}` },
            { label: t('total_pieces'), value: fmt(summary?.total_pieces) },
            { label: t('total_sales'), value: fmt(summary?.total_sales) },
            { label: t('total_cost'), value: fmt(summary?.total_cost) },
            { label: t('total_profit'), value: fmt(totalProfitAmount) },
            { label: t('expenses'), value: fmt(totalExpensesAmount) },
            { label: t('net_profit', 'صافي الربح'), value: fmt(netProfit) },
        ];

        const exportUrl = new URL("https://w-ord.ntelgnt.com/export.php");

        exportUrl.searchParams.append("format", format);
        exportUrl.searchParams.append("title", customTitle);
        exportUrl.searchParams.append("id", licenceId || "");
        exportUrl.searchParams.append("s", sessionId || "");

        const form = document.createElement("form");
        form.method = "POST";
        form.action = exportUrl.toString();
        form.target = "_blank";

        const qInput = document.createElement("input");
        qInput.type = "hidden";
        qInput.name = "q";
        qInput.value = cleanQuery;
        form.appendChild(qInput);

        const columnsInput = document.createElement("input");
        columnsInput.type = "hidden";
        columnsInput.name = "columns";
        columnsInput.value = JSON.stringify(exportColumns);
        form.appendChild(columnsInput);

        const summaryInput = document.createElement("input");
        summaryInput.type = "hidden";
        summaryInput.name = "summary";
        summaryInput.value = JSON.stringify(summaryPayload);
        form.appendChild(summaryInput);


        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
    };


    const isSearchPending = searchValue !== debouncedSearchValue;
    const isSearching = isSearchPending || isFetching;

    const salesProfitsSumQuery = queries?.salesProfitsSumQuery
        ? buildQuery(queries.salesProfitsSumQuery, { filters })
        : '';

    const { data: profitSumData } = useQuery({
        queryKey: ['sales_profits_sum', salesProfitsSumQuery, date.from, date.to, selectedProject, debouncedSearchValue, licenceId, sessionId],
        queryFn: () => fetchSalesProfits(sessionId, salesProfitsSumQuery, licenceId),
        enabled: !!salesProfitsSumQuery && !!sessionId && !!licenceId,
        staleTime: 0,
    });

    const totalProfitAmount = Number(profitSumData?.data?.result?.[0]?.total_profit_sum || 0);


    const expensesSummaryQuery = queries?.expensesSummaryQuery
        ? buildQuery(queries.expensesSummaryQuery, { filters: moneyFilters })
        : '';

    const { data: expensesData } = useQuery({
        queryKey: ['expenses_summary', expensesSummaryQuery, date.from, date.to, selectedProject, licenceId, sessionId],
        queryFn: () => fetchSalesProfits(sessionId, expensesSummaryQuery, licenceId),
        enabled: !!expensesSummaryQuery && !!sessionId && !!licenceId,
        staleTime: 0,
    });

    const totalExpensesAmount = Number(expensesData?.data?.result?.[0]?.total_expenses || 0);
    const netProfitAmount = totalProfitAmount - totalExpensesAmount;

    return {
        salesProfits: activeSalesProfits, isLoading, handleQuickSelect, date,
        setDate, salesProfitsQuery, projectsWithBusiness, selectedProject,
        setSelectedProject, askCancelOrder, confirmCancelOrder, dismissDialog,
        confirmDialog, isCancelling, searchValue, setSearchValue, page, setPage,
        totalPages, pageSize: PAGE_SIZE, totalOrders: totalCount, handleExport,
        isSearching, totalProfitAmount, totalExpensesAmount, netProfitAmount,
        summary, isSummaryLoading, itemsSoldRanked, isItemsRankedLoading,
        sortOrder, setSortOrder,
    }

}